/**
 * Utility functions for detecting location availability and permissions
 */

/**
 * Check if geolocation is available and what the permission status is
 * @returns {Promise<{available: boolean, permission: string|null, error: string|null}>}
 */
export async function checkLocationAvailability() {
  // Check if geolocation API exists
  if (!navigator.geolocation) {
    return {
      available: false,
      permission: null,
      error: 'Geolocation is not supported by your browser'
    };
  }

  // Try to use Permissions API if available (more accurate)
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      
      if (permissionStatus.state === 'denied') {
        return {
          available: false,
          permission: 'denied',
          error: 'Location access is denied. Please enable location services in your browser settings.'
        };
      }
      
      if (permissionStatus.state === 'prompt') {
        return {
          available: true,
          permission: 'prompt',
          error: null
        };
      }
      
      if (permissionStatus.state === 'granted') {
        return {
          available: true,
          permission: 'granted',
          error: null
        };
      }
    } catch (err) {
      // Permissions API might not be fully supported, fall back to geolocation test
      console.debug('Permissions API not fully supported, falling back to geolocation test:', err);
    }
  }

  // Fallback: Try a quick geolocation test
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      resolve({
        available: false,
        permission: null,
        error: 'Location services appear to be disabled or unavailable'
      });
    }, 2000); // 2 second timeout for quick check

    navigator.geolocation.getCurrentPosition(
      () => {
        clearTimeout(timeout);
        resolve({
          available: true,
          permission: 'granted',
          error: null
        });
      },
      (err) => {
        clearTimeout(timeout);
        let error = 'Location services unavailable';
        let permission = null;

        switch (err.code) {
          case err.PERMISSION_DENIED:
            permission = 'denied';
            error = 'Location access is denied. Please enable location services in your browser settings.';
            break;
          case err.POSITION_UNAVAILABLE:
            error = 'Location information is unavailable. Please check if location services are enabled on your device.';
            break;
          case err.TIMEOUT:
            error = 'Location request timed out. Location services may be disabled.';
            break;
          default:
            error = 'Unable to access location services.';
        }

        resolve({
          available: false,
          permission,
          error
        });
      },
      {
        enableHighAccuracy: false,
        timeout: 2000,
        maximumAge: 0 // Don't use cache for this check
      }
    );
  });
}

/**
 * Get a user-friendly error message for location errors
 * @param {GeolocationPositionError} error - The geolocation error object
 * @returns {string} User-friendly error message
 */
export function getLocationErrorMessage(error) {
  if (!error) {
    return 'Unknown location error';
  }

  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Location access is denied. Please enable location services in your browser settings and allow access for this site.';
    case error.POSITION_UNAVAILABLE:
      return 'Location information is unavailable. Please check if location services are enabled on your device.';
    case error.TIMEOUT:
      return 'Location request timed out. Location services may be disabled or unavailable.';
    default:
      return 'Unable to retrieve your location. Please check your location settings.';
  }
}

